import React from 'react';
import { 
  Shield, Lock, Key, Database, 
  CheckCircle, FileText, Server, Users,
  Mail, Cloud, AlertTriangle, Eye
} from 'lucide-react';
import Brand from '../components/Brand';

const SecurityBrief = () => {
  return (
    <>
      {/* Page 1 */}
      <div className="w-[8.5in] max-w-full h-[11in] mx-auto bg-white print:m-0 flex flex-col overflow-hidden page-break-after">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
          
          <div className="relative z-10">
            <Brand theme="dark" markClassName="text-white" className="text-3xl font-bold mb-4" />
            
            <h1 className="text-3xl font-bold mb-3">Security Brief</h1>
            <p className="text-lg text-green-50 max-w-4xl">
              Detailed security architecture, infrastructure compliance, and data policies.
            </p>
          </div>
          
          {/* Security Badge */}
          <div className="absolute top-6 right-6 bg-white text-green-600 p-3 rounded-full shadow-lg">
            <Shield className="h-8 w-8" />
          </div>
        </div>

        {/* Main Content - Page 1 */}
        <div className="flex-1 p-5">
          {/* 1. Security Architecture */}
          <div className="mb-5">
            <div className="flex items-center gap-3 mb-3">
              <Server className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">1. Security Architecture</h2>
            </div>
            
            <div className="ml-9 space-y-2">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Cloud className="h-5 w-5 text-blue-600" />
                  Infrastructure & Network
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Deployed on Render's cloud platform, leveraging AWS, GCP, and Cloudflare—providing DDoS protection, network segmentation, and managed host security.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>All external and inter-service traffic encrypted in transit via TLS 1.2+ with modern cipher suites.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Lock className="h-5 w-5 text-blue-600" />
                  Data Encryption
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-gray-900">At rest:</span>
                    <span>PostgreSQL volumes and object storage disks are AES-256 encrypted by default.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-gray-900">In use:</span>
                    <span>Analytics pipelines and logs are encrypted on disk and in snapshots.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Key className="h-5 w-5 text-blue-600" />
                  Secrets & Configuration
                </h3>
                <ul className="space-y-2 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Application secrets stored in encrypted environment variables; access restricted by workspace permissions.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Configuration changes audited via Render's Document Center logs.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 2. Authentication & Access Control */}
          <div className="mb-5">
            <div className="flex items-center gap-3 mb-3">
              <Users className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">2. Authentication & Access Control</h2>
            </div>
            
            <div className="ml-9 space-y-3">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Credential Security</h3>
                <ul className="space-y-2 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Passwords hashed using bcrypt with per-user salt and work factor ≥ 12.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>No plaintext passwords are ever persisted or logged.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Authorization</h3>
                <ul className="space-y-2 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Role-based access control (RBAC) enforced at API level with least-privilege principle.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Management interfaces protected by multi-factor authentication (MFA).</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Page 1 */}
        <div className="px-6 py-2 text-right">
          <span className="text-gray-500 text-sm">Page 1 of 2</span>
        </div>
      </div>

      {/* Page 2 */}
      <div className="w-[8.5in] max-w-full h-[11in] mx-auto bg-white print:m-0 flex flex-col overflow-hidden">
        {/* Simple Header for Page 2 */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <Brand theme="dark" markClassName="text-white" className="text-xl font-bold">HousingAI Security Brief</Brand>
            <Shield className="h-6 w-6" />
          </div>
        </div>

        {/* Main Content - Page 2 */}
        <div className="flex-1 p-5">
          {/* 3. Infrastructure Compliance */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">3. Infrastructure Compliance</h2>
            </div>

            <div className="ml-9 space-y-2">
              <p className="text-xs text-gray-600 mb-2">
                HousingAI is built on SOC 2 compliant infrastructure. The certifications below are
                held by our hosting and cloud providers and cover the underlying platform. They are
                not a HousingAI audit or attestation.
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-3">
                <h3 className="font-semibold text-gray-900 text-sm">SOC 2 Type II Infrastructure</h3>
                <p className="text-xs text-gray-700 mt-0.5">Our hosting provider maintains an annual third-party SOC 2 Type II audit of the platform controls our service runs on.</p>
              </div>
              <div className="bg-green-50 border-l-4 border-green-600 p-3">
                <h3 className="font-semibold text-gray-900 text-sm">ISO/IEC 27001:2022 Infrastructure</h3>
                <p className="text-xs text-gray-700 mt-0.5">Our hosting provider holds formal ISMS certification covering the infrastructure and operations supporting our service.</p>
              </div>
              <div className="bg-green-50 border-l-4 border-green-600 p-3">
                <h3 className="font-semibold text-gray-900 text-sm">HIPAA-Enabled Infrastructure</h3>
                <p className="text-xs text-gray-700 mt-0.5">Platform-level workspace controls for handling PHI, including strict host-access restrictions and BAA availability from the hosting provider.</p>
              </div>
              <div className="bg-green-50 border-l-4 border-green-600 p-3">
                <h3 className="font-semibold text-gray-900 text-sm">GDPR / DPA</h3>
                <p className="text-xs text-gray-700 mt-0.5">Platform-level EU-US Data Privacy Framework alignment for personal data processing.</p>
              </div>
            </div>
          </div>

          {/* 4. Data Policies */}
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <Database className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">4. Data Policies</h2>
            </div>
            
            <div className="ml-9 space-y-3">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Retention & Minimization</h3>
                <ul className="space-y-1 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Analytics/event logs retained 90 days; aggregated/anonymized after 30 days.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>PII separated from event data; only pseudonymized identifiers in analytics.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Encryption & Masking</h3>
                <ul className="space-y-1 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Sensitive fields (usernames, emails) encrypted at rest using AES-256.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Database backups encrypted and stored in access-controlled buckets.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Incident Response & Monitoring</h3>
                <ul className="space-y-1 text-sm text-gray-700 ml-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Continuous vulnerability scanning and intrusion detection monitoring.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>24×7 on-call rotation and HackerOne vulnerability disclosure program.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 5. Shared Responsibility Model */}
          <div className="mb-5">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
              <h2 className="text-xl font-bold text-gray-900">5. Shared Responsibility Model</h2>
            </div>
            
            <div className="ml-9 space-y-2">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-2">
                <h3 className="font-semibold text-gray-900 text-sm">Our Responsibility</h3>
                <p className="text-xs text-gray-700">Secure hypervisor, host OS, physical data centers, and foundational platform controls.</p>
              </div>
              <div className="bg-orange-50 border-l-4 border-orange-600 p-2">
                <h3 className="font-semibold text-gray-900 text-sm">Your Responsibility</h3>
                <p className="text-xs text-gray-700">Secure application code, enforce strong access rules, manage your own compliance artifacts.</p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="text-center mt-3">
            <div className="flex items-center justify-center gap-4">
              <h3 className="text-base font-bold text-gray-900">Contact Security Team:</h3>
              <div className="flex flex-wrap items-center gap-2">
                <Mail className="h-4 w-4 text-green-600" />
                <span className="text-base font-semibold text-green-600">security@gethousingai.com</span>
              </div>
            </div>
            <p className="text-sm text-gray-700 italic">Let's advance fair housing together!</p>
          </div>
        </div>

        {/* Footer - Page 2 */}
        <div className="bg-gray-900 text-white p-4 text-center mt-auto">
          <Brand theme="dark" markClassName="text-blue-600" className="justify-center text-xl font-bold mb-2" />
          <p className="text-gray-300">© 2026 HousingAI. All rights reserved.</p>
        </div>
      </div>

      <style jsx>{`
        @media print {
          .page-break-after {
            page-break-after: always;
          }
        }
      `}</style>
    </>
  );
};

export default SecurityBrief;